import { useCallback, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import {
  Button,
  Col,
  Col1,
  Content,
  Form,
  Row,
  TextBody,
  TextInput,
  TextTitle,
} from "../lib";
import { LoadingScreen } from "./loading";
import {
  getLocation,
  Location,
  Place,
  searchPlaces,
  useLocationStore,
  useUser,
  useUserLocationUpdate,
} from "../services";
import { CalloutRefresh, PlaceListItem } from "../components";
import { useNavigation } from "@react-navigation/native";
import { createStyles } from "../theme";

export function IncallScreen() {
  // Data
  const {
    data: user,
    isLoading: isUserLoading,
    refetch: refetchUser,
  } = useUser();
  const [storeLocation] = useLocationStore();
  const [updateUserLocation] = useUserLocationUpdate();

  // State
  const [search, setSearch] = useState("");
  const [isSaving, setSaving] = useState(false);
  const [places, setPlaces] = useState<Place[]>([]);
  const [uuid] = useState(uuidv4());
  const [isEditing, setEditing] = useState(false);

  // Vars
  const cityId = user?.city?.id;
  const navigation = useNavigation();

  // Handlers
  const handleChangeSearch = useCallback(
    async (_name: "search", newSearch: string | null) => {
      setSearch(newSearch ?? "");

      if (!newSearch) {
        setPlaces([]);
      } else if (newSearch.length >= 3 && cityId) {
        setPlaces(await searchPlaces({ q: newSearch, uuid, city_id: cityId }));
      }
    },
    [cityId, uuid]
  );

  const handlePressPlace = useCallback(
    async (placeId: string) => {
      let location: Location | null = null;

      try {
        setSaving(true);

        // Check if location already exists in DB
        location = await getLocation(placeId);
      } catch {
        location = await storeLocation({ place_id: placeId, uuid });
      } finally {
        if (location) {
          await updateUserLocation({ location_id: location.id });
        }

        setSaving(false);

        navigation.goBack();
      }
    },
    [navigation, storeLocation, updateUserLocation, uuid]
  );

  const handleEdit = useCallback(() => {
    setEditing(true);
  }, []);

  // Render
  if (isUserLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <CalloutRefresh onRefresh={refetchUser} />;
  }

  if (!isEditing && user.location) {
    return (
      <Content>
        <TextTitle mb="2">{user.location.name}</TextTitle>
        <TextBody>{user.location.address}</TextBody>
        <Row.L mt="6">
          <Button em onPress={handleEdit}>
            Update location
          </Button>
        </Row.L>
      </Content>
    );
  }

  return (
    <Form p="0">
      <Col p="4">
        <TextInput
          autoCorrect={false}
          autoFocus
          disabled={isSaving}
          name="search"
          onChange={handleChangeSearch}
          placeholder="Type address"
          returnKeyType="done"
          value={search}
        />
      </Col>
      <Col1 style={$.list}>
        {places.map((place) => (
          <PlaceListItem
            key={place.place_id}
            id={place.place_id}
            description={place.description}
            onPress={handlePressPlace}
          />
        ))}
      </Col1>
    </Form>
  );
}

const $ = createStyles({
  list: {
    backgroundColor: "surface",
  },
});
